import { API_PREFIX_MEDIA_PATH } from "@/constants/prefix-path-api"
import type { MediaData, Pagination } from "@/lib/config/types"
import { AxiosRequestConfig } from "axios"
import { HttpClient } from "../../../../app/api/HttpClient"

export const fetchMediaAll = async () => {
  return await HttpClient.get(`${API_PREFIX_MEDIA_PATH}/files`)
}

export const fetchMedia = async (
  page = 1,
  pageSize = 50,
  searchTerm = "",
  folderId?: number | null,
  domain?: string,
  sort = "createdAt:desc",
): Promise<{ data: MediaData[]; pagination: Pagination }> => {
  const params: AxiosRequestConfig["params"] = {
    sort,
    page: page,
    pageSize: pageSize,
    search: searchTerm,
  }
  // `null` = root (folder is null), matching the CMS default view; `undefined`
  // = no folder filter. A concrete id scopes to that folder.
  if (folderId === null) {
    params.folder = "null"
  } else if (folderId !== undefined) {
    params.folder = folderId
  }
  if (domain) {
    params.domain = domain
  }

  const resp = await HttpClient.get<{ data: MediaData[]; meta: { pagination: Pagination } }>(`/api/media`, { params })
  return { data: resp?.data ?? [], pagination: resp?.meta?.pagination ?? { pageSize, current: 1, total: 0 } }
}

export const fetchMediaId = async (id: number, domain?: string) => {
  const params: AxiosRequestConfig["params"] = {}
  if (domain) params.domain = domain
  return await HttpClient.get(`${API_PREFIX_MEDIA_PATH}/files/${id}`, { params })
}

export const deleteMedia = async (id: number, domain?: string) => {
  const params: AxiosRequestConfig["params"] = {}
  if (domain) params.domain = domain
  return await HttpClient.delete(`${API_PREFIX_MEDIA_PATH}/files/${id}`, { params })
}

export const updateMedia = async (id: number, data: FormData, domain?: string) => {
  const BASE_URL = process.env.NEXT_PUBLIC_API_URL ?? ""
  const params = new URLSearchParams({ id: String(id) })
  if (domain) params.set("domain", domain)

  if (domain) {
    data.set("domain", domain)
  }

  const url = `${BASE_URL}${API_PREFIX_MEDIA_PATH}?${params.toString()}`
  const token = localStorage.getItem("auth_token")
  const response = await fetch(url, {
    method: "POST",
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: data,
  })
  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.message || `Update failed: ${response.status}`)
  }
  return await response.json()
}

export const addMedia = async (data: FormData, folderId?: number | null, domain?: string): Promise<MediaData[]> => {
  const BASE_URL = process.env.NEXT_PUBLIC_API_URL ?? ""
  const params = new URLSearchParams()
  if (domain) params.set("domain", domain)

  if (domain) {
    data.set("domain", domain)
  }

  // A concrete folderId uploads into that folder; null/undefined uploads to the
  // library root — mirroring the CMS: admin uploads into the folder currently
  // open, and to root when standing at "Tất cả tệp".
  const uploadPath =
    folderId != null ? `/api/media/folders/${folderId}/uploads` : `/api/media/uploads`
  const API_PATH = `${BASE_URL}${uploadPath}${params.toString() ? `?${params}` : ""}`
  const token = localStorage.getItem("auth_token")

  const response = await fetch(API_PATH, {
    method: "POST",
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: data,
  })

  const responseData = await response.json()
  if (!response.ok) throw new Error(responseData.message || `Upload failed: ${response.status}`)
  return Array.isArray(responseData) ? responseData : responseData?.data ?? []
}

export const bulkDeleteMedia = async (ids: number[], domain?: string) => {
  const BASE_URL = process.env.NEXT_PUBLIC_API_URL ?? ""
  const params = new URLSearchParams()
  if (domain) params.set("domain", domain)
  const url = `${BASE_URL}/api/media/bulk-delete${params.toString() ? `?${params}` : ""}`
  const token = localStorage.getItem("auth_token")

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ ids, ...(domain ? { domain } : {}) }),
  })

  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.message || `Bulk delete failed: ${response.status}`)
  }

  return response.status === 204 ? null : await response.json()
}

/** POST `/api/media/folders/{folderId}/uploads` — move/copy existing media into folder (body: media entities). */
export const handleBulkMove = async (folderId: number, media: MediaData[], domain?: string) => {
  const BASE_URL = process.env.NEXT_PUBLIC_API_URL ?? ""
  const params = new URLSearchParams()
  if (domain) params.set("domain", domain)
  const url = `${BASE_URL}/api/media/folders/${folderId}/uploads${params.toString() ? `?${params}` : ""}`
  const token = localStorage.getItem("auth_token")

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(media),
  })

  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.message || `Bulk move failed: ${response.status}`)
  }

  const ct = response.headers.get("content-type")
  if (ct?.includes("application/json")) {
    return await response.json()
  }
  return null
}

export const moveMediaToFolder = async (ids: number[], targetFolderId: number, domain?: string) => {
  const BASE_URL = process.env.NEXT_PUBLIC_API_URL ?? ""
  const params = new URLSearchParams()
  if (domain) params.set("domain", domain)
  const url = `${BASE_URL}/api/media/move-to-folder${params.toString() ? `?${params}` : ""}`
  const token = localStorage.getItem("auth_token")

  const response = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ ids, targetFolderId, ...(domain ? { domain } : {}) }),
  })

  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.message || `Move to folder failed: ${response.status}`)
  }

  const ct = response.headers.get("content-type")
  if (ct?.includes("application/json")) {
    return await response.json()
  }
  return null
}